import { action, computed, observable, toJS } from "mobx";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import { BaseStore } from "@store/BaseStore";
import { BisnodePersonFragment, BisnodeLookup_Phone, BisnodeLookup_SSN } from "@graphql";

type Callback = () => void;

export type LookupItem = {
  checked: boolean;
  disabled: boolean;
  field: keyof BisnodePersonFragment;
  title: string;
  currentValue: string;
  newValue: string;
};

export class BisnodeLookupStore extends BaseStore {
  @observable open = false;
  @observable lookupItems: LookupItem[] = [];

  private currentValues: Record<keyof BisnodePersonFragment, string>;
  private onSubmitCallback: Callback;
  private onCancelCallback: Callback;

  constructor() {
    super();
    this.setReady();
  }

  @computed get allChecked(): boolean {
    if (!this.lookupItems || this.lookupItems.length === 0) {
      return false;
    }

    return this.lookupItems.filter((it) => !it.disabled && !it.checked).length === 0;
  }

  @computed get allDisabled() {
    if (!this.lookupItems || this.lookupItems.length === 0) {
      return true;
    }

    return this.lookupItems.filter((it) => !it.disabled).length === 0;
  }

  @action.bound checkAll() {
    if (this.allChecked) {
      this.lookupItems = this.lookupItems.map((it) => ({
        ...it,
        checked: false
      }));
    } else {
      this.lookupItems = this.lookupItems.map((it) => ({
        ...it,
        checked: it.disabled ? false : true
      }));
    }
  }

  @action.bound setCurrentValues(values: Record<keyof BisnodePersonFragment, string>) {
    this.currentValues = values;
  }

  @action.bound phoneLookup(country: string, phone: string) {
    this.setLoading();

    return this.lookup(() =>
      BisnodeLookup_Phone.func({ country, phone }, { fetchPolicy: "network-only" }).then(
        (result) => result.data.lookup
      )
    );
  }

  @action.bound ssnLookup(country: string, ssn: string) {
    this.setLoading();

    return this.lookup(() =>
      BisnodeLookup_SSN.func({ country, ssn }, { fetchPolicy: "network-only" }).then(
        (result) => result.data.lookup
      )
    );
  }

  @action.bound checkItem(item: LookupItem) {
    const _item = this.lookupItems.find((it) => it.field === item.field);

    if (!_item) {
      return;
    }

    _item.checked = !_item.checked;
  }

  @action.bound onSubmit() {
    this.onSubmitCallback && this.onSubmitCallback();
    this.close();
  }

  @action.bound onCancel() {
    this.onCancelCallback && this.onCancelCallback();
    this.close();
  }

  @action.bound private lookup(cb: () => Promise<BisnodePersonFragment>) {
    return new Promise<{ items?: LookupItem[] }>((resolve, reject) => {
      cb()
        .then((data) => {
          if (!data) {
            store().snackbars.info(`Unfortunately, nothing was found`);
            return;
          }

          this.lookupItems = this.resultToLookupItems(data);
          this.open = true;
          this.onSubmitCallback = () =>
            resolve({ items: toJS(this.lookupItems.filter((x) => x.checked)) });
          this.onCancelCallback = () => resolve({});
        })
        .catch((e) => {
          store().snackbars.error(`Cannot fetch bisnode results\nReason: ${e}`);
        })
        .finally(() => this.setReady());
    });
  }

  @action.bound private close() {
    this.lookupItems = [];
    this.open = false;
  }

  private resultToLookupItems(data: BisnodePersonFragment) {
    const result: LookupItem[] = [];

    const addToResult = (field: keyof BisnodePersonFragment, title: string) => {
      const newValue = data[field];
      const currentValue = this.currentValues[field];

      if (!newValue) {
        return;
      }

      result.push({
        checked: newValue !== currentValue,
        disabled: newValue === currentValue,
        field,
        title,
        newValue,
        currentValue
      });
    };

    addToResult("FirstName", locale.FirstName);
    addToResult("LastName", locale.LastName);
    addToResult("CellPhone", locale.CellPhone);
    addToResult("Phone", locale.Phone);
    addToResult("AddressCity", locale.City);
    addToResult("AddressCountry", locale.Country);
    addToResult("AddressStreetName", locale.StreetName);
    addToResult("AddressZipCode", locale.ZipCode);

    return result;
  }
}
